import type { BaseAPIResponse } from '~/types/api';
import type {
    ConsolidationCancelSummary,
    ConsolidationPreview,
    ConsolidationScheduleSummary,
    ConsolidationStatus,
} from '~/types/consolidation';
import { pbRequest, pbRequestRaw } from '~/services/request';

// Consolidated billing — aligning every auto-renewing seat onto one shared
// renewal date.
export const consolidationService = {
    /**
     * Consolidated billing (license renewal alignment).
     *
     * Preview the per-seat extension estimate schedule for aligning every
     * auto-renewing seat onto one shared renewal date. Read-only and server-
     * computed; pass a `sharedDate` to estimate against a specific date, else the
     * backend defaults to the furthest-out current renewal. Amounts are estimates.
     */
    async previewConsolidation(companyId: number, sharedDate?: string): Promise<ConsolidationPreview> {
        const params: Record<string, unknown> = {};
        if (sharedDate) params.shared_date = sharedDate;
        return await pbRequest<ConsolidationPreview>(`/companies/${companyId}/financial/consolidation/preview`, {
            params,
            failure: 'Failed to preview consolidation',
        });
    },

    /**
     * Schedule a consolidation onto `sharedDate`. Scheduling COSTS MONEY (PB-074): one
     * invoice is raised, a line per license block, and collected from the card on file or
     * left open on the company's terms. The summary reports it as `invoice` — optional,
     * because an API that predates PB-074 omits it, and null when every extension landed
     * on an invoice that already existed.
     *
     * 422 `consolidation_costs_nothing` when every license already renews on the shared date;
     * 409 `consolidation_active` when another administrator scheduled first, 409
     * `payment_in_progress` while a payment on one of the licenses is still settling, and 409
     * `payer_busy` while another payment holds the company's payer lock.
     */
    async scheduleConsolidation(companyId: number, sharedDate: string): Promise<ConsolidationScheduleSummary> {
        return await pbRequest<ConsolidationScheduleSummary>(
            `/companies/${companyId}/financial/consolidation/schedule`,
            {
                method: 'POST',
                body: { shared_date: sharedDate },
                failure: 'Failed to schedule consolidation',
            }
        );
    },

    /**
     * Cancel the active consolidation. Unpaid seats revert to a full-term
     * renewal; seats already paid-and-extended keep their advanced date. Returns
     * the reverted/stayed split for the post-cancel message.
     */
    async cancelConsolidation(companyId: number): Promise<ConsolidationCancelSummary> {
        return await pbRequest<ConsolidationCancelSummary>(`/companies/${companyId}/financial/consolidation/cancel`, {
            method: 'POST',
            body: {},
            failure: 'Failed to cancel consolidation',
        });
    },

    /**
     * The active consolidation's status (N-of-M aligned, next expected invoice,
     * per-seat state), or `null` when none is active — the backend returns
     * `{ active: false }` in that case.
     */
    async getConsolidation(companyId: number): Promise<ConsolidationStatus | null> {
        const envelope = await pbRequestRaw<BaseAPIResponse<ConsolidationStatus | { active: false }>>(
            `/companies/${companyId}/financial/consolidation`,
            {
                failure: 'Failed to fetch consolidation',
            }
        );

        const payload = envelope.data as ConsolidationStatus | { active: false };
        if (!payload || (payload as { active?: boolean }).active === false) {
            return null;
        }
        return payload as ConsolidationStatus;
    },
};
